import { FormState, useForm, UseFormMethods } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { ObjectSchema } from 'yup';

interface GetFieldPropsArgs<FormData> {
  id: keyof FormData; // FormData -> key;
  name: string; // FormData -> key -> key
  translationKey: string;
}
interface UseFormResponse<FormData> {
  getFieldProps: (args: GetFieldPropsArgs<FormData>) => void;
  handleSubmit: UseFormMethods['handleSubmit'];
  resetForm: UseFormMethods['reset'];
  control: UseFormMethods['control'];
  watch: UseFormMethods['watch'];
  setValue: UseFormMethods['setValue'];
  getValues: UseFormMethods['getValues'];
  formState: FormState<FormData>;
}

/**
 * Wraps react-hook-form in some defaults
 */
export default <FormData>({
  validationSchema = null,
}: // defaultValues = null,
{
  validationSchema?: ObjectSchema;
  defaultValues?: unknown;
} = {}): UseFormResponse<FormData> => {
  const {
    register,
    control,
    watch,
    handleSubmit,
    reset,
    formState,
    setValue,
    getValues,
    errors: fieldErrors,
  } = useForm<FormData>({
    resolver: validationSchema && yupResolver(validationSchema),
    // defaultValues,
    // shouldUseNativeValidation: true, // Allows :valid and :invalid in CSS
  });

  /**
   * Get the props that are the same for all input fields
   * @param {string} id The name of the 'field set' e.g. 'address'
   * @param {string} name The unique name of the input e.g. 'line_one'
   * @param {string} translationKey The 'address' in t(`forms:labels.address.line_one`)
   */
  const getFieldProps = ({
    id,
    name,
    translationKey = '',
  }: GetFieldPropsArgs<FormData>) => {
    // eslint-disable-next-line
    // @ts-ignore
    const errors = fieldErrors && fieldErrors[id] && fieldErrors[id][name];

    // When not using a validationSchema, validation is set as each field is 'registerd' (by react-hook-form)
    // e.g. <input {...register('test', { required: true })} />
    // In these instances, we don't have a `message`, this adds it
    if (errors && !errors.message) {
      errors.message = {
        // TODO: make this dynamic
        key: 'field_required',
      };
    }

    return {
      id,
      name,
      translationKey: `${translationKey || (id as string)}.${name}`,
      errors,
      register,
      control,
    };
  };

  return {
    getFieldProps,
    handleSubmit,
    resetForm: reset,
    control,
    watch,
    formState,
    setValue,
    getValues,
  };
};
